import type { IdentityOrigin, UserType } from '@/features/users/userApi'
import { apiSlice } from '@/store/api/apiSlice'

export interface ProvisioningOptionItem {
  Id: number
  Value: string
}

export interface ProvisioningOptionsResponse {
  Template: ProvisioningOptionItem[]
  Type: ProvisioningOptionItem[]
  PricingPlan: ProvisioningOptionItem[]
}

export interface ProvisioningPayload {
  template_id: number
  type_id: number
  pricing_plan_id: number
}

export interface CreateAccountRequest {
  account: {
    keycloak_realm_id: string
    display_name: string
    contact_email: string
    is_active: boolean
    workflow_team_member_count: number
  }
  admin_user: {
    email: string
    first_name: string
    last_name: string
    locale: string
    timezone: string
  }
  /** WF provisioning options; optional at API level for other callers */
  provisioning?: ProvisioningPayload
}

/** Create-account payload when using the WF provisioning flow; provisioning is required and always sent. */
export type CreateAccountWFRequest = CreateAccountRequest & {
  provisioning: ProvisioningPayload
}

export interface ApplicationAccountStatus {
  application: string
  status: 'provisioning' | 'ready' | 'error'
  error_message: string
}

export interface ProvisioningStatusResponse {
  account_id: number
  status: 'provisioning' | 'ready' | 'partial_error'
  application_accounts: ApplicationAccountStatus[]
}

/** Response returned by the create endpoint (202 Accepted). */
export interface CreateAccountAsyncResponse {
  account_id: number
}

export interface UpdateAccountRequest {
  display_name?: string
  contact_email?: string
  is_active?: boolean
}

export interface AccountDetailsResponse {
  account: {
    id: number
    display_name: string
    contact_email: string | null
    keycloak_realm_id: string
    keycloak_jwks_url: string
    is_active: boolean
  }
  application_accounts: Array<{
    application: {
      id: number
      name: string
    }
    status: string
    application_account_id: string
  }>
}

export interface AccountListItem {
  id: number
  display_name: string
  contact_email: string | null
  keycloak_realm_id: string
  keycloak_jwks_url: string
  is_active: boolean
}

/** One member row of an account: an account user, with the identity fields inline. */
export interface AccountUser {
  id: number
  account_id: number
  email: string
  first_name: string
  last_name: string
  /** 'First Last', the one non-blank name, or the email. Never persisted server-side. */
  display_name: string
  title?: string | null
  phone?: string | null
  locale: string
  timezone: string
  identity_origin: IdentityOrigin
  /** Non-null: identity fields are owned by the organisation's IdP and read-only here. */
  sso_linked_at: string | null
  user_type: UserType
  status: string
  keycloak_user_id: string | null
  is_active: boolean
  application_users: Array<{
    application: {
      id: number
      name: string
    }
    application_user_id: string
  }>
}

export const accountApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getProvisioningOptions: builder.query<ProvisioningOptionsResponse, void>({
      query: () => '/public/v1/staff/accounts/provisioning-options/',
      transformResponse: (raw: Record<string, unknown>) => {
        const toList = (v: unknown): ProvisioningOptionItem[] => {
          if (!Array.isArray(v)) return []
          return v
            .map(
              (x: {
                Id?: number
                id?: number
                Value?: string
                value?: string
              }) => {
                const rawId = x?.Id ?? x?.id
                const rawValue = x?.Value ?? x?.value
                if (rawId == null || rawValue == null) return null
                const numericId =
                  typeof rawId === 'number' ? rawId : Number(rawId)
                const stringValue =
                  typeof rawValue === 'string'
                    ? rawValue.trim()
                    : String(rawValue).trim()
                if (!Number.isFinite(numericId) || stringValue === '') {
                  return null
                }
                return { Id: numericId, Value: stringValue }
              },
            )
            .filter((item): item is ProvisioningOptionItem => item !== null)
        }
        return {
          Template: toList(raw?.Template ?? raw?.template),
          Type: toList(raw?.Type ?? raw?.type),
          PricingPlan: toList(
            raw?.PricingPlan ?? raw?.pricing_plan ?? raw?.pricingPlan,
          ),
        }
      },
    }),
    createAccount: builder.mutation<
      CreateAccountAsyncResponse,
      CreateAccountRequest
    >({
      query: body => ({
        url: '/public/v1/staff/accounts/create/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    getAccountDetails: builder.query<
      AccountDetailsResponse,
      number | undefined
    >({
      query: id => `/public/v1/staff/accounts/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Account', id }],
    }),
    updateAccount: builder.mutation<
      AccountDetailsResponse,
      { id: number; data: UpdateAccountRequest }
    >({
      query: ({ id, data }) => ({
        url: `/public/v1/staff/accounts/${id}/update/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Account', id }],
    }),
    getAccountUsers: builder.query<AccountUser[], number | undefined>({
      query: id => `/public/v1/accounts/${id}/users/`,
      providesTags: result => [
        { type: 'AccountUser' as const, id: 'LIST' },
        ...(result ?? []).map(m => ({
          type: 'AccountUser' as const,
          id: m.id,
        })),
        ...(result ?? []).map(m => ({ type: 'User' as const, id: m.id })),
      ],
    }),
    getProvisioningStatus: builder.query<ProvisioningStatusResponse, number>({
      query: accountId =>
        `/public/v1/staff/accounts/${accountId}/provisioning-status/`,
    }),
    getAccounts: builder.query<AccountListItem[], void>({
      query: () => '/public/v1/staff/accounts/',
      providesTags: ['Account'],
    }),
  }),
})

export const {
  useCreateAccountMutation,
  useGetAccountDetailsQuery,
  useGetAccountsQuery,
  useGetProvisioningOptionsQuery,
  useGetProvisioningStatusQuery,
  useUpdateAccountMutation,
  useGetAccountUsersQuery,
} = accountApi

// Custom hook wrappers that handle skip logic
export const useAccountDetails = (id: number | undefined) => {
  return useGetAccountDetailsQuery(id, {
    skip: !id,
  })
}

export const useAccountUsers = (id: number | undefined) => {
  return useGetAccountUsersQuery(id, {
    skip: !id,
  })
}
